import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { ChevronDown } from 'lucide-react'

const PaymentTabs = ({ 
  userRole, 
  allowedRoles,
  showPaypal,
  onTabChange 
}) => {
  const [activeTab, setActiveTab] = useState('new-card')
  const [showPreviousCards, setShowPreviousCards] = useState(false)

  // Check if user has permission to see tabs
  const hasAccess = allowedRoles.includes(userRole?.toLowerCase())

  if (!hasAccess) {
    return null
  }

  const handleTabClick = (tabId) => {
    setActiveTab(tabId)
    if (onTabChange) {
      onTabChange(tabId)
    }
  }

  const tabs = [
    { id: 'new-card', label: 'Add new card', icon: '+' },
    { id: 'previous-cards', label: 'Previous cards', hasDropdown: true },
    { id: 'club-billing', label: 'Club Billing', role: ['pro', 'admin']},
  ]

  const filteredTabs = tabs.filter(tab => {
    if (tab.role) {
      return tab.role.includes(userRole?.toLowerCase())
    }
    return true
  })

  if (showPaypal) {
    filteredTabs.push({ id: 'paypal', label: 'PayPal' })
  }

  return (
    <div className="w-full mb-6">
      <div className="flex gap-2 border-b border-gray-200 pb-2">
        {filteredTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              if (tab.hasDropdown) {
                setShowPreviousCards(!showPreviousCards)
              }
              handleTabClick(tab.id)
            }}
            className={`
              flex items-center gap-1.5 px-4 mt-4 py-0 text-sm font-medium 
              rounded transition-all duration-200 relative
              ${activeTab === tab.id 
                ? 'text-blue-600' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }
            `}
            type="button"
            aria-label={tab.label}
            aria-pressed={activeTab === tab.id}
          >
            {tab.icon && <span className="text-base font-semibold">{tab.icon}</span>}
            <span>{tab.label}</span>
            {tab.hasDropdown && (
              <ChevronDown 
                size={16} 
                className={`transition-transform duration-200 ${
                  showPreviousCards ? 'rotate-180' : ''
                }`}
                aria-hidden="true"
              />
            )}
            {activeTab === tab.id && (
              <span 
                className="absolute bottom-[-0.5rem] left-0 right-0 h-0.5 bg-blue-600"
                aria-hidden="true"
              />
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

PaymentTabs.propTypes = {
  /**
   * Current user's role - used to determine if tabs should be visible
   */
  userRole: PropTypes.string,
  
  /**
   * Array of roles that are allowed to see these tabs
   */
  allowedRoles: PropTypes.arrayOf(PropTypes.string),
  
  /**
   * Whether to show the PayPal tab
   */
  showPaypal: PropTypes.bool,
  
  /**
   * Callback function called when tab changes
   * Receives the tab id as argument
   */
  onTabChange: PropTypes.func,
}

PaymentTabs.defaultProps = {
  userRole: 'user',
  allowedRoles: ['admin', 'pro'],
  showPaypal: false,
  onTabChange: null,
}

export default PaymentTabs